import React, { useEffect, useState } from 'react';
import { X, Loader, Wand2, Check, AlertTriangle, CheckCircle2 } from 'lucide-react';
import apiClient from '../../config/api.config';

interface Props {
  etudiantId: number;
  matricule: string;
  nom: string;
  niveau: string;
  classe: string;
  semestre: number;
  moyenneActuelle: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface UeEligibilite {
  ue_code: string;
  ue_nom: string;
  moyenne_ue: number;
  valide: boolean;
  ecues: Array<{
    matiere_id: number;
    matiere_nom: string;
    matiere_code: string;
    note_examen: number | null;
    moyenne_ecue: number;
    eligible_rachat: boolean;
    rachat_max: number | null;
  }>;
}

interface AnalyseResponse {
  parametres: Record<string, string>;
  etudiants: Array<{
    etudiant_id: number;
    matricule: string;
    nom: string;
    moyenne_semestre: number;
    valide: boolean;
    eligible: boolean;
    ues: UeEligibilite[];
  }>;
}

const ModalRachatIntelligent: React.FC<Props> = ({
  etudiantId, matricule, nom, niveau, classe, semestre,
  moyenneActuelle, onClose, onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [analyse, setAnalyse] = useState<AnalyseResponse['etudiants'][0] | null>(null);
  const [parametres, setParametres] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    const fetchAnalyse = async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await apiClient.post('/rachat-intelligent/analyser/', {
          niveau, semestre, classe,
        });
        const data: AnalyseResponse = res.data;
        setParametres(data.parametres);
        const e = data.etudiants.find(x => x.etudiant_id === etudiantId || x.matricule === matricule);
        setAnalyse(e || null);
      } catch (e: any) {
        setErr(e?.response?.data?.error || e?.message || 'Erreur d\'analyse');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalyse();
  }, [etudiantId, matricule, niveau, semestre, classe]);

  const handleProposer = async () => {
    if (!etudiantId) return;
    setApplying(true);
    setErr(null);
    try {
      const res = await apiClient.post('/rachat-intelligent/proposer-rachats/', {
        etudiants: [etudiantId],
        niveau, semestre,
      });
      setResult(res.data);
      onSuccess();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erreur lors de la proposition');
    } finally {
      setApplying(false);
    }
  };

  const seuil = parseFloat(parametres.seuil_admission || '10');
  const deficit = moyenneActuelle != null ? Math.max(0, seuil - moyenneActuelle) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Wand2 size={18} className="text-purple-600" />
              Rachat intelligent · {nom}
            </h3>
            <div className="text-xs text-gray-500 mt-0.5">
              {matricule} · {niveau} · {classe} · Semestre {semestre}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-50 dark:bg-gray-900 rounded p-2">
              <div className="text-[10px] text-gray-500">Moyenne actuelle</div>
              <div className={`text-lg font-bold ${
                (moyenneActuelle ?? 0) >= seuil ? 'text-green-600' : 'text-red-600'
              }`}>
                {moyenneActuelle != null ? moyenneActuelle.toFixed(2) : '—'}
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2">
              <div className="text-[10px] text-blue-500">Seuil admission</div>
              <div className="text-lg font-bold text-blue-700 dark:text-blue-300">{seuil}</div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded p-2">
              <div className="text-[10px] text-amber-500">Déficit à combler</div>
              <div className="text-lg font-bold text-amber-700 dark:text-amber-300">
                {deficit > 0 ? `+${deficit.toFixed(2)}` : '0.00'}
              </div>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader size={18} className="animate-spin mr-2" /> Analyse en cours…
            </div>
          )}

          {err && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-sm flex items-center gap-2">
              <AlertTriangle size={14} /> {err}
            </div>
          )}

          {result && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-sm">
              <div className="flex items-center gap-2 font-medium mb-1">
                <CheckCircle2 size={14} /> Rachat proposé avec succès.
              </div>
              <pre className="text-[10px] bg-white/50 dark:bg-black/20 p-2 rounded mt-1 overflow-x-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}

          {analyse && !result && (
            <>
              {analyse.valide ? (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-sm flex items-center gap-2">
                  <CheckCircle2 size={16} />
                  Ce semestre est déjà validé. Aucun rachat nécessaire.
                </div>
              ) : !analyse.eligible ? (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded text-sm flex items-center gap-2">
                  <AlertTriangle size={16} />
                  Cet étudiant n'est pas éligible au rachat (moyenne trop faible ou autre contrainte).
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                    UE du semestre :
                  </div>
                  <div className="space-y-2">
                    {(analyse.ues || []).map(ue => (
                      <div
                        key={ue.ue_code}
                        className={`border rounded-lg p-3 ${
                          ue.valide
                            ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800'
                            : 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-sm">
                            <span className="font-mono text-xs text-gray-500 mr-2">{ue.ue_code}</span>
                            {ue.ue_nom}
                          </div>
                          <span className={`text-sm font-bold ${ue.valide ? 'text-green-600' : 'text-red-600'}`}>
                            {Number(ue.moyenne_ue).toFixed(2)}
                          </span>
                        </div>
                        <div className="space-y-0.5">
                          {(ue.ecues || []).map(ec => (
                            <div key={ec.matiere_id} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 dark:text-gray-300">
                                <span className="font-mono text-gray-400 mr-1">{ec.matiere_code}</span>
                                {ec.matiere_nom}
                              </span>
                              <div className="flex items-center gap-2">
                                <span>Exam : <strong>{ec.note_examen ?? '—'}</strong></span>
                                <span>Moy : <strong>{Number(ec.moyenne_ecue).toFixed(2)}</strong></span>
                                {ec.eligible_rachat && ec.rachat_max != null && (
                                  <span className="text-purple-600 dark:text-purple-400 text-[10px]">
                                    (max +{Number(ec.rachat_max).toFixed(2)})
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200">
            Fermer
          </button>
          {analyse && !analyse.valide && analyse.eligible && !result && (
            <button onClick={handleProposer} disabled={applying}
              className="px-4 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded flex items-center gap-1">
              {applying ? <Loader size={12} className="animate-spin" /> : <Check size={14} />}
              Proposer les rachats automatiques
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalRachatIntelligent;
